import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowLeftRight, Calendar, Users, Search, Plane, MapPin, Loader2 } from "lucide-react";
import { BangladeshFlag, SwedenFlag } from "@/components/Flags";

const TP_MARKER = process.env.REACT_APP_TP_MARKER || "";

const POPULAR_ROUTES = [
  { from: "STO", fromName: "Stockholm", to: "DAC", toName: "Dhaka", price: "from 6,950 kr" },
  { from: "STO", fromName: "Stockholm", to: "DXB", toName: "Dubai", price: "from 3,400 kr" },
  { from: "STO", fromName: "Stockholm", to: "IST", toName: "Istanbul", price: "from 2,800 kr" },
  { from: "DAC", fromName: "Dhaka", to: "STO", toName: "Stockholm", price: "from 580 USD" },
  { from: "STO", fromName: "Stockholm", to: "BKK", toName: "Bangkok", price: "from 5,200 kr" },
  { from: "STO", fromName: "Stockholm", to: "DOH", toName: "Doha", price: "from 4,100 kr" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

const fmtAviasalesDate = (iso) => {
  // YYYY-MM-DD → DDMM
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function CityField({ label, value, onChange, placeholder, testId }) {
  const [query, setQuery] = useState(value?.label || "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (value?.label) setQuery(value.label);
  }, [value]);

  useEffect(() => {
    if (query.length < 2) {
      setOpts([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `https://autocomplete.travelpayouts.com/places2`,
          { params: { term: query, locale: "en", "types[]": ["city", "airport"] } }
        );
        if (!cancelled) setOpts(Array.isArray(data) ? data.slice(0, 8) : []);
      } catch {
        if (!cancelled) setOpts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (o) => {
    const code = o.code || o.iata || o.city_code;
    const label = `${o.name}${o.country_name ? `, ${o.country_name}` : ""}`;
    onChange({ code, label });
    setQuery(label);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <label className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--ivory-muted)] mb-1.5 block">{label}</label>
      <div className="relative">
        <MapPin className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--ivory-muted)] pointer-events-none" />
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); onChange(null); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="email-input pl-10"
          autoComplete="off"
          data-testid={testId}
        />
        {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[color:var(--ivory-muted)]" />}
      </div>
      {open && opts.length > 0 && (
        <div className="absolute z-30 mt-1 left-0 right-0 rounded-xl border border-white/10 bg-[#0E1216] shadow-2xl overflow-hidden max-h-72 overflow-y-auto" data-testid={`${testId}-options`}>
          {opts.map((o, i) => (
            <button
              type="button"
              key={`${o.code}-${i}`}
              onClick={() => pick(o)}
              className="w-full text-left px-4 py-2.5 hover:bg-white/[0.04] flex items-center gap-3 text-sm border-b border-white/5 last:border-b-0"
            >
              <Plane className="w-3.5 h-3.5 text-[color:var(--bd-green-bright)] shrink-0" />
              <span className="flex-1">
                <span className="text-white">{o.name}</span>
                {o.country_name && <span className="text-[color:var(--ivory-muted)] ml-1">· {o.country_name}</span>}
              </span>
              <span className="text-xs font-mono text-[color:var(--ivory-muted)]">{o.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Flights() {
  const [tripType, setTripType] = useState("round");
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [departDate, setDepartDate] = useState(addDaysISO(14));
  const [returnDate, setReturnDate] = useState(addDaysISO(21));
  const [passengers, setPassengers] = useState(1);
  const [cabin, setCabin] = useState("Y"); // Y=economy, C=business
  const [error, setError] = useState("");

  const swap = () => {
    const a = origin, b = destination;
    setOrigin(b); setDestination(a);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!origin?.code || !destination?.code) {
      setError("Please pick both From and To airports from the suggestions.");
      return;
    }
    setError("");
    const dep = fmtAviasalesDate(departDate);
    const ret = tripType === "round" ? fmtAviasalesDate(returnDate) : "";
    const pax = Math.max(1, Math.min(9, Number(passengers) || 1));
    // Aviasales URL format: /search/{O}{DEP}{D}{RET}{PAX}{CABIN}
    // Example: STO1512DAC2212 1 (economy)
    const path = `${origin.code}${dep}${destination.code}${ret}${pax}${cabin === "C" ? "C" : ""}`;
    const params = new URLSearchParams({
      ...(TP_MARKER ? { marker: TP_MARKER } : {}),
      currency: "sek",
      locale: "en",
    });
    const url = `https://www.aviasales.com/search/${path}?${params.toString()}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <div data-testid="flights-page">
      <section className="relative">
        <div className="bg-stage" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="bg-grid" />
          <div className="orb orb-green" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pt-16 pb-12 sm:pt-24">
          <div className="flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-[color:var(--ivory-muted)] mb-3">
            <BangladeshFlag className="w-6 h-4 rounded-sm" />
            <span>Bangladesh</span>
            <Plane className="w-3 h-3" />
            <SwedenFlag className="w-6 h-4 rounded-sm" />
            <span>Sweden &amp; worldwide flights</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Search flights.<br />
            <span className="text-[color:var(--bd-green-bright)]">Compare</span> 700+ airlines.
          </h1>
          <p className="mt-4 max-w-2xl text-[color:var(--ivory-muted)] text-base sm:text-lg">
            Find the cheapest fares to Dhaka, Stockholm and 200+ cities. We search real-time across hundreds of airlines.
          </p>
        </div>
      </section>

      <section className="relative -mt-2 sm:-mt-6 pb-16">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12">
          <form
            onSubmit={submit}
            className="p-5 sm:p-7 rounded-3xl border border-white/10 bg-[#0E1216]/85 backdrop-blur-md shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
            data-testid="flight-search-form"
          >
            {/* Trip type tabs */}
            <div className="flex items-center gap-1 mb-5">
              <TripTab active={tripType === "round"} onClick={() => setTripType("round")} testId="trip-round">Round trip</TripTab>
              <TripTab active={tripType === "oneway"} onClick={() => setTripType("oneway")} testId="trip-oneway">One way</TripTab>
            </div>

            <div className="grid lg:grid-cols-[1fr_auto_1fr_auto] gap-3 items-end">
              <CityField label="From" value={origin} onChange={setOrigin} placeholder="City or airport" testId="flight-from" />
              <button type="button" onClick={swap} className="hidden lg:flex items-center justify-center w-11 h-11 mb-0 rounded-full bg-white/[0.04] border border-white/10 hover:bg-[color:var(--bd-green)]/15 transition" data-testid="flight-swap" aria-label="Swap">
                <ArrowLeftRight className="w-4 h-4" />
              </button>
              <CityField label="To" value={destination} onChange={setDestination} placeholder="City or airport" testId="flight-to" />
              <button type="button" onClick={swap} className="lg:hidden pill border border-white/15 text-xs">Swap</button>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--ivory-muted)] mb-1.5 block">Depart</label>
                <div className="relative">
                  <Calendar className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--ivory-muted)] pointer-events-none" />
                  <input type="date" min={todayISO()} value={departDate} onChange={(e) => setDepartDate(e.target.value)} className="email-input pl-10" data-testid="flight-depart" />
                </div>
              </div>
              <div className={tripType === "oneway" ? "opacity-40 pointer-events-none" : ""}>
                <label className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--ivory-muted)] mb-1.5 block">Return</label>
                <div className="relative">
                  <Calendar className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--ivory-muted)] pointer-events-none" />
                  <input type="date" min={departDate} value={returnDate} onChange={(e) => setReturnDate(e.target.value)} className="email-input pl-10" data-testid="flight-return" />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--ivory-muted)] mb-1.5 block">Passengers</label>
                <div className="relative">
                  <Users className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--ivory-muted)] pointer-events-none" />
                  <input type="number" min={1} max={9} value={passengers} onChange={(e) => setPassengers(e.target.value)} className="email-input pl-10" data-testid="flight-pax" />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--ivory-muted)] mb-1.5 block">Class</label>
                <select value={cabin} onChange={(e) => setCabin(e.target.value)} className="email-input pl-4 appearance-none cursor-pointer" data-testid="flight-class">
                  <option value="Y" style={{ background: "#0E1216" }}>Economy</option>
                  <option value="C" style={{ background: "#0E1216" }}>Business</option>
                </select>
              </div>
            </div>

            {error && <div className="mt-4 text-sm text-[color:var(--bd-red-bright)]" data-testid="flight-error">{error}</div>}

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs text-[color:var(--ivory-muted)]">
                We compare fares across 700+ airlines via our travel network. Bookings open in a new tab.
              </p>
              <button type="submit" className="pill pill-primary inline-flex items-center gap-2 justify-center" data-testid="flight-search-submit">
                <Search className="w-4 h-4" /> Search flights
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Popular routes */}
      <section className="pb-20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <h2 className="font-display text-3xl sm:text-4xl mb-8">Popular routes</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {POPULAR_ROUTES.map((r, i) => (
              <button
                key={i}
                onClick={() => {
                  setOrigin({ code: r.from, label: r.fromName });
                  setDestination({ code: r.to, label: r.toName });
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="text-left p-5 rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[color:var(--bd-green)]/30 transition group"
                data-testid={`route-${r.from}-${r.to}`}
              >
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-white font-medium">{r.fromName}</span>
                  <Plane className="w-3.5 h-3.5 text-[color:var(--bd-green-bright)]" />
                  <span className="text-white font-medium">{r.toName}</span>
                </div>
                <div className="mt-2 text-xs text-[color:var(--ivory-muted)] flex items-center justify-between">
                  <span className="font-mono">{r.from} → {r.to}</span>
                  <span className="text-[color:var(--bd-green-bright)] group-hover:text-white transition">{r.price}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function TripTab({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2 rounded-full text-sm transition ${
        active
          ? "bg-[color:var(--bd-green)]/20 text-white border border-[color:var(--bd-green)]/40"
          : "text-[color:var(--ivory-muted)] hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}
